import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {Bar, BarChart, Cell, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis} from 'recharts';
import type {ColDef} from 'ag-grid-community';
import {ArrowRight, ChevronLeft, ChevronRight, AlertCircle} from 'lucide-react';
import {InstitutionalDataGrid} from '../../../components/shared/InstitutionalDataGrid';
import {displayValue} from './AnalysisSections';
import {copilotApi, copilotError} from './copilotApi';
import type {BridgePayload, ComparisonPayload, CopilotArtifact, GridColumn, GridPayload, PoolSeriesPayload, TimelinePayload, TrendPayload, WaterfallPayload} from './types';
import { words } from './words';

/*
 * Typed artifact renderers (FRD v2 §7.2). Every figure shown is a server value; chart
 * coordinates are derived only to place bars and lines, never displayed as new numbers.
 */

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 12px; font-variant-numeric: tabular-nums; margin: 8px 0 4px;
  th {text-align: left; font-weight: 500; color: #9fb2cc; padding: 6px 8px; border-bottom: 1px solid #2b3b50;}
  td {padding: 6px 8px; border-bottom: 1px solid #1f2d40; color: #e2e8f0;}
  td.num, th.num {text-align: right; white-space: nowrap;}
  tr.total td {font-weight: 600; border-top: 1px solid #3a4c62;}
`;
const Note = styled.p`font-size: 12px; color: #9fb2cc; line-height: 1.55; margin: 6px 0 0;`;
const Badge = styled.span<{$tone: 'good' | 'bad' | 'warn' | 'plain'}>`font-size: 10px; letter-spacing: .04em; padding: 1px 6px; border-radius: 8px; white-space: nowrap;
  color: ${p => p.$tone === 'good' ? '#97d8ba' : p.$tone === 'bad' ? '#f4b6a7' : p.$tone === 'warn' ? '#dfc48f' : '#b7c5d8'};
  border: 1px solid ${p => p.$tone === 'good' ? '#2f5d4a' : p.$tone === 'bad' ? '#6b3a33' : p.$tone === 'warn' ? '#6a5731' : '#3a4c62'};`;
const Small = styled.div`font-size: 11px; color: #94a3b8;`;

export { words };
function tone(state: string | null | undefined): 'good' | 'bad' | 'warn' | 'plain' {
  const s = String(state || '').toUpperCase();
  if(['PASS', 'MATCHED', 'POSTED', 'RECONCILED', 'AVAILABLE', 'CLEARED', 'YES', 'RECORDED', 'APPLIED', 'SUCCESS', 'DISPOSITIONED'].includes(s)) return 'good';
  if(['FAIL', 'BREACH', 'VARIANCE', 'BLOCKED', 'FAILED', 'NOT_ON_STATEMENT', 'NOT_IN_WATERFALL', 'NO', 'GAP'].includes(s)) return 'bad';
  if(['WARN', 'PARTIAL', 'RESIDUAL', 'PENDING', 'OPEN', 'CARRIED', 'INDETERMINATE', 'NOT_AVAILABLE', 'WAIVED'].includes(s)) return 'warn';
  return 'plain';
}
export function StateBadge({state}: {state: string | null | undefined}) {
  return state ? <Badge $tone={tone(state)}>{words(state)}</Badge> : null;
}
const num = (value: string | null | undefined) => value === null || value === undefined || value === '' ? null : Number(value);
const compact = (value: number) => {
  const abs = Math.abs(value);
  return abs >= 1e9 ? `${(value / 1e9).toFixed(1)}B` : abs >= 1e6 ? `${(value / 1e6).toFixed(1)}M` : abs >= 1e3 ? `${(value / 1e3).toFixed(0)}K` : `${value}`;
};
function money(value: string | null | undefined, unit?: string | null) {
  return value === null || value === undefined ? 'Not available' : displayValue(value, unit);
}

/** start → signed steps → (residual) → end, drawn as a floating-bar waterfall with the table beneath. */
export function BridgeView({body, expanded}: {body: BridgePayload; expanded?: boolean}) {
  const bars = useMemo(() => {
    const rows: {name: string; base: number; value: number; color: string}[] = [];
    const start = num(body.start);
    if(start === null) return rows;
    rows.push({name: body.startLabel, base: 0, value: start, color: '#5b8fc7'});
    let running = start;
    const steps = [...body.steps];
    if(body.residual && num(body.residual) !== 0) steps.push({label: 'Holds, floor or unexplained', amount: body.residual, citationId: null});
    for(const step of steps) {
      const amount = num(step.amount);
      if(amount === null) continue;
      rows.push({name: step.label, base: amount < 0 ? running + amount : running, value: Math.abs(amount), color: amount < 0 ? '#c9786a' : '#6fb592'});
      running += amount;
    }
    const end = num(body.end);
    if(end !== null) rows.push({name: body.endLabel, base: 0, value: end, color: '#5b8fc7'});
    return rows;
  }, [body]);
  return <div>
    {bars.length > 1 && <div style={{height: expanded ? 320 : 40 + bars.length * 30}} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={bars} layout="vertical" margin={{top: 4, right: 12, bottom: 4, left: 4}}>
          <XAxis type="number" tickFormatter={compact} stroke="#64748b" fontSize={10} />
          <YAxis type="category" dataKey="name" width={expanded ? 220 : 150} stroke="#64748b" fontSize={10} />
          <Bar dataKey="base" stackId="bridge" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="value" stackId="bridge" isAnimationActive={false}>{bars.map((bar, i) => <Cell key={i} fill={bar.color} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>}
    <Table><tbody>
      <tr><td>{body.startLabel}</td><td className="num">{money(body.start, body.unit)}</td></tr>
      {body.steps.map((step, i) => <tr key={i}><td>{step.label}</td><td className="num">{money(step.amount, body.unit)}</td></tr>)}
      {body.residual !== null && num(body.residual) !== 0 && <tr><td>Holds, floor or unexplained</td><td className="num">{money(body.residual, body.unit)}</td></tr>}
      <tr className="total"><td>{body.endLabel} <StateBadge state={body.outcome} /></td><td className="num">{money(body.end, body.unit)}</td></tr>
    </tbody></Table>
    {body.note && <Note>{body.note}</Note>}
  </div>;
}

export function ComparisonView({body}: {body: ComparisonPayload}) {
  return <Table>
    <thead><tr><th>Metric</th><th className="num">{body.priorLabel}</th><th className="num">{body.currentLabel}</th><th className="num">Change</th></tr></thead>
    <tbody>{body.rows.map(row => <tr key={row.label}>
      <td>{row.label}</td><td className="num">{money(row.prior, row.unit)}</td><td className="num">{money(row.current, row.unit)}</td>
      <td className="num" style={{color: num(row.delta) ? (num(row.delta)! < 0 ? '#f4b6a7' : '#97d8ba') : undefined}}>{row.delta === null ? '—' : money(row.delta, row.unit)}</td>
    </tr>)}</tbody>
  </Table>;
}

/** A covenant figure in its unit: "3.41%", "$1,350,000"; never the stored eight decimals (F-LR-062). */
function covenantFigure(value: string | null | undefined, unit: string | null | undefined): string {
  const n = num(value ?? null);
  if(n === null || Number.isNaN(n)) return displayValue(value ?? null);
  if(unit === '%') return `${n.toFixed(2)}%`;
  return displayValue(Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2), unit);
}

/** One small chart per covenant: signed cushion by period, zero line = threshold. */
export function TrendView({body, expanded}: {body: TrendPayload; expanded?: boolean}) {
  return <div>
    <Table>
      <thead><tr><th>Covenant</th><th>Latest</th><th className="num">Actual</th><th className="num">Threshold</th><th className="num">Cushion</th><th className="num">Prior cushion</th></tr></thead>
      <tbody>{body.series.map(series => {
        const last = series.points[series.points.length - 1];
        const prior = series.points.length > 1 ? series.points[series.points.length - 2] : null;
        return <tr key={series.key}>
          <td>{series.name}<Small>{words(series.thresholdType)}</Small></td><td><StateBadge state={last?.state} /></td>
          <td className="num">{covenantFigure(last?.actual, series.unit)}</td><td className="num">{covenantFigure(last?.threshold, series.unit)}</td>
          <td className="num">{covenantFigure(last?.cushion, series.unit)}</td><td className="num">{prior ? covenantFigure(prior.cushion, series.unit) : '—'}</td>
        </tr>;
      })}</tbody>
    </Table>
    <div style={{display: 'grid', gridTemplateColumns: expanded ? 'repeat(auto-fill, minmax(240px, 1fr))' : 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginTop: 8}} aria-hidden="true">
      {body.series.filter(series => series.points.filter(p => p.cushion !== null).length > 1).map(series => {
        const data = series.points.map(point => ({period: point.period, cushion: num(point.cushion)}));
        return <div key={series.key}><Small>{series.name} · cushion</Small>
          <div style={{height: expanded ? 120 : 80}}><ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{top: 6, right: 8, bottom: 0, left: 0}}>
              <XAxis dataKey="period" stroke="#64748b" fontSize={9} /><YAxis hide domain={['auto', 'auto']} />
              <ReferenceLine y={0} stroke="#c9786a" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="cushion" stroke="#7fc4e0" dot={{r: 2}} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer></div>
        </div>;
      })}
    </div>
    <Note>Stored test results; below the dashed line the covenant is outside its threshold. Waived or indeterminate is not a pass.</Note>
  </div>;
}

/** Our priority of payments against the agent's statement, node by node, with paired bars. */
export function WaterfallView({body}: {body: WaterfallPayload}) {
  const max = Math.max(1, ...body.nodes.flatMap(node => [Math.abs(num(node.ours) || 0), Math.abs(num(node.agent) || 0)]));
  const width = (value: string | null) => `${Math.round(Math.abs(num(value) || 0) / max * 100)}%`;
  return <div>
    <Table>
      <thead><tr><th>Node</th><th className="num">Ours</th><th className="num">Agent</th><th className="num">Variance</th><th /></tr></thead>
      <tbody>
        {body.nodes.map((node, i) => <tr key={i}>
          <td>{node.node}<div style={{marginTop: 4}} aria-hidden="true">
            <div style={{height: 4, width: width(node.ours), background: '#5b8fc7', borderRadius: 2}} />
            <div style={{height: 4, width: width(node.agent), background: '#b39ddb', borderRadius: 2, marginTop: 2}} />
          </div></td>
          <td className="num">{money(node.ours, body.currency)}</td><td className="num">{money(node.agent, body.currency)}</td>
          <td className="num">{money(node.variance, body.currency)}</td><td><StateBadge state={node.state} /></td>
        </tr>)}
        <tr className="total"><td>Total <StateBadge state={body.state} /></td><td className="num">{money(body.ourTotal, body.currency)}</td>
          <td className="num">{money(body.agentTotal, body.currency)}</td><td className="num">{money(body.totalVariance, body.currency)}</td><td /></tr>
      </tbody>
    </Table>
    <Note><span style={{color: '#5b8fc7'}}>■</span> ours · <span style={{color: '#b39ddb'}}>■</span> paying agent · tolerance {money(body.tolerance, body.currency)}</Note>
  </div>;
}

const Steps = styled.ol`list-style: none; margin: 8px 0 0; padding: 0 0 0 14px; border-left: 1px solid #334155;
  li {position: relative; padding: 0 0 12px 12px; font-size: 12px; line-height: 1.5;}
  li::before {content: ''; position: absolute; left: -19px; top: 5px; width: 9px; height: 9px; border-radius: 50%; background: #5b8fc7;}
  li.gap::before {background: #dfc48f;}
  li.gap {color: #dfc48f;}
  button {font: inherit; color: #9bdef5; background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px;}
`;
/** Request → event → trade → posting → lots; links that cannot be proven are shown as gaps. */
export function TimelineView({body}: {body: TimelinePayload}) {
  const navigate = useNavigate();
  return <Steps>{body.events.map((event, i) => <li key={i} className={event.gap ? 'gap' : undefined}>
    {event.gap && <AlertCircle size={12} style={{marginRight: 4, verticalAlign: -1}} />}
    <strong style={{fontWeight: 500}}>{event.label}</strong> {!event.gap && <StateBadge state={event.state} />}
    <Small>{[event.at, event.detail].filter(Boolean).join(' · ')}</Small>
    {event.route && <button type="button" onClick={() => navigate(event.route!)}>Open record <ArrowRight size={11} /></button>}
  </li>)}</Steps>;
}

/** Grid state survives moving between the inline card and the focused view. */
const gridStates = new Map<string, {page: number; sort?: string; desc?: boolean; q?: string}>();

function format(column: GridColumn, value: unknown): string {
  if(value === null || value === undefined || value === '') return '—';
  switch(column.type) {
    case 'money': return displayValue(String(value), column.unit);
    case 'pct': return displayValue(String(value), '%');
    case 'num': return displayValue(String(value));
    case 'status': return words(value);
    default: return String(value);
  }
}

/** Server-paged rows behind a saved answer (FRD v2 ART-002); starts from the saved preview. */
export function GridView({artifact, turnId, body, expanded}: {artifact: CopilotArtifact; turnId: string; body: GridPayload; expanded?: boolean}) {
  const navigate = useNavigate();
  const size = 20;
  const saved = gridStates.get(artifact.id) || {page: 0, sort: body.defaultSort || undefined, desc: true};
  const [state, setState] = useState(saved);
  const [rows, setRows] = useState<Record<string, unknown>[]>(body.preview || []);
  const [total, setTotal] = useState(body.rowCount);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const first = useRef(true);
  useEffect(() => {gridStates.set(artifact.id, state);}, [artifact.id, state]);
  useEffect(() => {
    // The saved preview is page one in the default order; fetch only when the reader asks for more.
    const isDefault = state.page === 0 && !state.q && state.sort === (body.defaultSort || undefined) && state.desc !== false;
    if(first.current && isDefault) {first.current = false; return;}
    first.current = false;
    const controller = new AbortController(); setLoading(true); setError('');
    copilotApi.gridRows(turnId, artifact.id, {page: state.page, size, sort: state.sort, desc: state.desc, q: state.q || undefined}, controller.signal)
      .then(result => {if(!controller.signal.aborted) {setRows(result.rows); setTotal(result.total);}})
      .catch(e => {if(!controller.signal.aborted) setError(copilotError(e));})
      .finally(() => {if(!controller.signal.aborted) setLoading(false);});
    return () => controller.abort();
  }, [turnId, artifact.id, state, body.defaultSort]);
  const search = useRef<number>();
  const columnDefs = useMemo<ColDef[]>(() => body.columns.map(column => ({
    field: column.field, headerName: column.header, minWidth: column.type === 'text' ? 150 : 110,
    type: ['money', 'pct', 'num'].includes(column.type) ? 'rightAligned' : undefined,
    valueFormatter: params => format(column, params.value),
  })), [body.columns]);
  const exportCsv = async () => {
    try {
      const blob = await copilotApi.gridExport(turnId, artifact.id);
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `${artifact.title.replace(/[^A-Za-z0-9-]+/g, '-')}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch(e) {setError(copilotError(e));}
  };
  const from = total === 0 ? 0 : state.page * size + 1;
  const to = Math.min(total, (state.page + 1) * size);
  return <div>
    <InstitutionalDataGrid rowData={rows} columnDefs={columnDefs} height={expanded ? 560 : 340} gridId={`copilot-${body.tool}`}
      enableSavedViews={false} enableDensity={expanded} pagination={false} emptyLabel={loading ? 'Loading…' : 'No rows match'}
      searchPlaceholder="Search these rows"
      onRowClicked={row => {const route = (row as Record<string, unknown>)._route; if(typeof route === 'string' && route.startsWith('/portfolio-management/credit/abf/')) navigate(route);}}
      serverSide={{
        onSortChanged: sort => setState(old => ({...old, page: 0, sort: sort?.field || body.defaultSort || undefined, desc: sort ? sort.desc : true})),
        onSearchChanged: text => {window.clearTimeout(search.current); search.current = window.setTimeout(() => setState(old => ({...old, page: 0, q: text.trim() || undefined})), 300);},
        onExport: exportCsv,
      }} />
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6}}>
      <Small>{loading ? 'Loading…' : `${from}–${to} of ${total}`} · select a row to open it</Small>
      <div style={{display: 'flex', gap: 4}}>
        <PageButton aria-label="Previous page" disabled={loading || state.page === 0} onClick={() => setState(old => ({...old, page: old.page - 1}))}><ChevronLeft size={14} /></PageButton>
        <PageButton aria-label="Next page" disabled={loading || to >= total} onClick={() => setState(old => ({...old, page: old.page + 1}))}><ChevronRight size={14} /></PageButton>
      </div>
    </div>
    {error && <Note role="alert" style={{color: '#fcd34d'}}>{error}</Note>}
  </div>;
}
const PageButton = styled.button`display: inline-grid; place-items: center; width: 28px; height: 26px; border: 1px solid #3a4c62; border-radius: 6px; background: #152438; color: #d2e4f3; cursor: pointer; &:disabled {opacity: .4; cursor: default;}`;

/** WS2: one small chart per pool metric, the stored points only; the table beneath carries the figures. */
export function PoolSeriesView({body, expanded}: {body: PoolSeriesPayload; expanded?: boolean}) {
  const figure = (value: string | null, unit: string | null, kind: string) => value === null ? '—' : kind === 'ratio' ? `${num(value)?.toFixed(2)}%` : money(value, unit);
  return <div>
    <Table>
      <thead><tr><th>Metric</th><th className="num">{body.fromDate}</th><th className="num">{body.toDate}</th><th className="num">Change</th></tr></thead>
      <tbody>{body.series.map(series => {
        const first = series.points[0], last = series.points[series.points.length - 1];
        const delta = first?.value !== null && last?.value !== null && first && last ? (num(last.value) ?? 0) - (num(first.value) ?? 0) : null;
        return <tr key={series.key}>
          <td>{series.name}{last?.basis && series.key === 'utilization' ? <Small>base {words(last.basis)}</Small> : null}</td>
          <td className="num">{figure(first?.value ?? null, series.unit, series.kind)}</td>
          <td className="num">{figure(last?.value ?? null, series.unit, series.kind)}</td>
          <td className="num">{delta === null ? '—' : series.kind === 'ratio' ? `${delta >= 0 ? '+' : ''}${delta.toFixed(2)} pts` : money(String(delta), series.unit)}</td>
        </tr>;
      })}</tbody>
    </Table>
    <div style={{display: 'grid', gridTemplateColumns: expanded ? 'repeat(auto-fill, minmax(240px, 1fr))' : 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginTop: 8}} aria-hidden="true">
      {body.series.filter(series => series.points.filter(p => p.value !== null).length > 1).map(series => {
        const data = series.points.map(point => ({date: point.date.slice(2, 7), value: num(point.value)}));
        return <div key={series.key}><Small>{series.name}</Small>
          <div style={{height: expanded ? 120 : 80}}><ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{top: 6, right: 8, bottom: 0, left: 0}}>
              <XAxis dataKey="date" stroke="#64748b" fontSize={9} /><YAxis hide domain={['auto', 'auto']} />
              <Line type="monotone" dataKey="value" stroke="#7fc4e0" dot={{r: 2}} isAnimationActive={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer></div>
        </div>;
      })}
    </div>
    {body.note && <Note>{body.note}</Note>}
  </div>;
}

export const TYPED_SCHEMAS = ['METRIC_BRIDGE/1', 'COMPARISON_TABLE/1', 'COVENANT_TREND/1', 'WATERFALL/1', 'LINEAGE_TIMELINE/1', 'DATA_GRID/1', 'POOL_SERIES/1'];

/** Renders a typed artifact, or null when the artifact has no typed payload (legacy renderers apply). */
export function TypedArtifact({artifact, turnId, expanded}: {artifact: CopilotArtifact; turnId: string; expanded?: boolean}) {
  const payload = artifact.payload as {schema?: string; body?: unknown} | null | undefined;
  switch(payload?.schema) {
    case 'METRIC_BRIDGE/1': return <BridgeView body={payload.body as BridgePayload} expanded={expanded} />;
    case 'COMPARISON_TABLE/1': return <ComparisonView body={payload.body as ComparisonPayload} />;
    case 'COVENANT_TREND/1': return <TrendView body={payload.body as TrendPayload} expanded={expanded} />;
    case 'WATERFALL/1': return <WaterfallView body={payload.body as WaterfallPayload} />;
    case 'LINEAGE_TIMELINE/1': return <TimelineView body={payload.body as TimelinePayload} />;
    case 'DATA_GRID/1': return <GridView artifact={artifact} turnId={turnId} body={payload.body as GridPayload} expanded={expanded} />;
    case 'POOL_SERIES/1': return <PoolSeriesView body={payload.body as PoolSeriesPayload} expanded={expanded} />;
    default: return null;
  }
}
